import React, { useState, useContext } from 'react';
import { Box, IconButton, Autocomplete, TextField, CircularProgress, Divider } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

import useUpdateChangeTicket from '../hooks/vendors/useUpdateChangeTicket';
import useVendorSearch from '../hooks/vendors/useVendorSearch';
import useDebouncedValue from '../hooks/useDebouncedValue';
import { AppContext } from '../../App';

interface EditableStandardVendorNameProps {
  ticketId: number;
  currentValue: string | null;
  discoveredValue?: string;
}

const USE_DISCOVERED_LABEL = 'Use Discovered';

const EditableStandardVendorName: React.FC<EditableStandardVendorNameProps> = ({
  ticketId,
  currentValue,
  discoveredValue,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(currentValue || '');
  const [selectedValue, setSelectedValue] = useState<string | null>(currentValue || null);

  const { setToast } = useContext(AppContext);

  // Server-side vendor search: only queries once the user is editing and has
  // typed something, so mounting a table of rows fires no requests.
  const debouncedQuery = useDebouncedValue(inputValue, 400);
  const { data: vendorResults, isFetching: vendorsLoading } = useVendorSearch({
    search: debouncedQuery,
    limit: 20,
    enabled: isEditing && debouncedQuery.trim().length > 0,
  });

  const { mutate: updateChangeTicket, isPending: isLoading } = useUpdateChangeTicket({
    onSuccess: () => {
      setIsEditing(false);
      setToast({
        variant: 'success',
        open: true,
        message: 'Vendor name updated successfully',
      });
    },
    onError: (error) => {
      console.error('Mutation error:', error);
      setToast({
        variant: 'error',
        open: true,
        message: 'Failed to update vendor name. Please try again.',
      });
    },
  });

  const vendorNames = vendorResults?.results.map((vendor) => vendor.name) || [];
  const vendorOptions = [
    USE_DISCOVERED_LABEL,
    ...vendorNames.filter((v) => v !== USE_DISCOVERED_LABEL),
  ];

  const handleEdit = () => {
    setIsEditing(true);
    setInputValue(currentValue || '');
    setSelectedValue(currentValue || null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setInputValue(currentValue || '');
    setSelectedValue(currentValue || null);
  };

  const handleSave = () => {
    const trimmed = inputValue.trim();
    const valueToPersist = trimmed === '' ? null : trimmed;
    updateChangeTicket({ id: ticketId, standard_vendor_name: valueToPersist });
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSave();
    } else if (event.key === 'Escape') {
      handleCancel();
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Autocomplete
        freeSolo
        data-cy={`autocomplete-standard-vendor-name-${discoveredValue}`}
        options={vendorOptions}
        // Results are already filtered server-side (incl. alias matches), so
        // don't let MUI hide them by re-filtering against the input text.
        filterOptions={(opts) => opts}
        loading={vendorsLoading}
        value={selectedValue}
        onChange={(event, newValue) => {
          if (newValue === USE_DISCOVERED_LABEL) {
            const resolved = discoveredValue || '';
            setSelectedValue(resolved || null);
            setInputValue(resolved);
          } else {
            setSelectedValue(newValue);
            setInputValue(newValue || '');
          }
        }}
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        getOptionLabel={(option) => option || ''}
        onFocus={handleEdit}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          return (
            <React.Fragment key={key}>
              <li {...optionProps}>{option}</li>
              {option === USE_DISCOVERED_LABEL && vendorOptions.length > 1 && (
                <Divider component="li" sx={{ my: 0.5 }} />
              )}
            </React.Fragment>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            variant="outlined"
            onKeyPress={handleKeyPress}
            placeholder="Enter vendor name"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {vendorsLoading ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            sx={{
              minWidth: 320,
              '& .MuiInputBase-root': {
                backgroundColor: isEditing ? 'white' : '#f5f5f5',
                '& fieldset': {
                  borderColor: isEditing ? undefined : '#e0e0e0',
                },
                '&:hover fieldset': {
                  borderColor: isEditing ? undefined : '#e0e0e0',
                },
                '& input': {
                  color: isEditing ? 'inherit' : '#666666',
                  fontSize: '0.875rem',
                  padding: '6px 8px',
                },
              },
            }}
          />
        )}
      />
      <IconButton
        size="small"
        onClick={handleSave}
        disabled={isLoading || !isEditing}
        color="primary"
        data-cy={`btn-save-standard-vendor-name-${discoveredValue}`}
      >
        <CheckIcon />
      </IconButton>
      <IconButton
        size="small"
        onClick={handleCancel}
        disabled={isLoading || !isEditing}
        color="error"
        data-cy="cancel-vendor-name"
      >
        <CloseIcon />
      </IconButton>
    </Box>
  );
};

export default EditableStandardVendorName;
